import Link from "next/link";
import { ReactNode } from "react-markdown";

import { Action, Category } from "@/constants/events";
import { Dispensary } from "@/custom-types/Dispensary";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getEducationalMaterials } from "@/redux/selectors/complianceRules";
import addUtmParamsToDispensaryWebsite from "@/utils/dispensary/addUtmParamsToDispensaryWebsite";
import getDispensaryPrimaryAddress from "@/utils/dispensary/getDispensaryPrimaryAddress";
import isDispensaryDeliveryOnly from "@/utils/dispensary/isDispensaryDeliveryOnly";

import AwardBadgeIcon from "@/components/Icons/award_badge.svg";
import CartIcon from "@/components/Icons/cart.svg";
import FileIcon from "@/components/Icons/file.svg";
import HeartOutlineIcon from "@/components/Icons/heart_outline.svg";
import OpenInNewIcon from "@/components/Icons/open_in_new.svg";
import PhoneIcon from "@/components/Icons/phone.svg";
import EmailIcon from "@/components/Icons/share/email.svg";
import WebsiteIcon from "@/components/Icons/website_charcoal.svg";

import DispensaryFeatures from "../DispensaryFeatures";
import PrimaryAddress from "../PrimaryAddress";

interface RetailerInfoSectionProps {
  dispensary: Dispensary;
  forModal?: boolean;
}

type Label = "email" | "visit website" | "call" | "direction";

const RetailerInfoSection: React.FC<RetailerInfoSectionProps> = ({
  dispensary,
  forModal,
}) => {
  const { publishEvent } = useEventTracker();
  const {
    created,
    description,
    name,
    phone,
    id,
    website,
    featureTier,
    email,
    followerCount,
    licenseNumber,
    leaflyListYears,
    acceptsCash,
    acceptsCreditCards,
    acceptsDebitCards,
    tags,
  } = dispensary;
  const joinedYear = new Date(created).getFullYear();
  const trackAboutGAEvent = (label: Label) => {
    publishEvent({
      action: Action.click,
      category: Category.dispensaryFooter,
      dispensaryId: id,
      label: label,
    });
  };
  const websiteWithUTM = addUtmParamsToDispensaryWebsite(website);
  const isInfoTier = featureTier === "info";
  const paymentOptions = acceptsCash || acceptsCreditCards || acceptsDebitCards;
  const primaryAddress = getDispensaryPrimaryAddress(dispensary);
  const isDeliveryOnly = isDispensaryDeliveryOnly(tags);
  const showDirections = primaryAddress && !isDeliveryOnly;

  const educationalMaterials = useSelector(getEducationalMaterials);

  return (
    <div>
      {description && forModal && (
        <Section title={`About ${name}`}>
          <div data-testid="retailer-description">{description}</div>
        </Section>
      )}
      {forModal && <hr className="border-light-grey my-lg" />}
      <Section title={`Leafly member since ${joinedYear}`}>
        <ul className="flex flex-col gap-lg">
          {showDirections && (
            <ListItem>
              <PrimaryAddress dispensary={dispensary} />
            </ListItem>
          )}
          {phone && (
            <ListItem icon={<PhoneIcon width="24" height="24" />}>
              <Link
                data-testid={"about_dispensary__call"}
                onClick={() => {
                  trackAboutGAEvent("call");
                }}
                href={`tel:${phone}`}
                className="underline"
              >
                call {phone}
              </Link>
            </ListItem>
          )}
          {websiteWithUTM && !isInfoTier && (
            <ListItem icon={<WebsiteIcon />}>
              <span className="flex gap-xs">
                <Link
                  data-testid={"about_dispensary__visit_website"}
                  onClick={() => trackAboutGAEvent("visit website")}
                  href={websiteWithUTM}
                  rel="sponsored noreferrer"
                  target="_blank"
                  className="underline"
                >
                  visit website
                </Link>
                <OpenInNewIcon width="24" height="24" />
              </span>
            </ListItem>
          )}
          {educationalMaterials?.licenseVerificationLink && !forModal && (
            <ListItem icon={<WebsiteIcon />}>
              <span className="flex gap-xs">
                <Link
                  data-testid="edu-materials-link"
                  href={educationalMaterials.licenseVerificationLink.url}
                  rel="external"
                  target="_blank"
                  className="underline"
                >
                  {educationalMaterials.licenseVerificationLink.text}
                </Link>
                <OpenInNewIcon width="24" height="24" />
              </span>
            </ListItem>
          )}
          {email && !isInfoTier && (
            <ListItem icon={<EmailIcon />}>
              <Link
                data-testid={"about_dispensary__email"}
                onClick={() => trackAboutGAEvent("email")}
                href={`mailto:${email}`}
                className="underline"
              >
                send an email
              </Link>
            </ListItem>
          )}
          {!!followerCount && (
            <ListItem icon={<HeartOutlineIcon width="24" height="24" />}>
              Followers: {followerCount}
            </ListItem>
          )}
          {paymentOptions && (
            <ListItem icon={<CartIcon />}>
              <span className="first-letter:uppercase [&>*:not(:last-child)]:after:content-[',_']">
                {acceptsCreditCards && <span>credit card</span>}
                {acceptsDebitCards && <span>debit card</span>}
                {acceptsCash && <span>cash</span>}
              </span>
            </ListItem>
          )}
          {licenseNumber && (
            <ListItem icon={<FileIcon />}>
              <span className="whitespace-normal break-all">
                License {licenseNumber}
              </span>
            </ListItem>
          )}
          {!!tags.length && (
            <ListItem data-testid="modal_dispensary_tags">
              <DispensaryFeatures dispensary={dispensary} />
            </ListItem>
          )}
        </ul>
      </Section>
      {!!leaflyListYears.length && forModal && (
        <div>
          <hr className="border-light-grey my-lg" />
          <Section title="leafly list awards">
            <ul className="flex flex-col gap-sm">
              {leaflyListYears.map((year: number) => (
                <li className="flex items-center gap-xs" key={year}>
                  <AwardBadgeIcon width={28} height={28} />
                  <span className="font-bold text-xs">
                    {year}&nbsp;Leafly List Winner
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      )}
    </div>
  );
};

const Section: React.FC<{ children: ReactNode; title: string }> = ({
  children,
  title,
}) => {
  return (
    <div className="flex flex-col gap-sm">
      <div className="font-extrabold text-xs uppercase">{title}</div>
      {children}
    </div>
  );
};

const ListItem: React.FC<{ children: ReactNode; icon?: ReactNode }> = ({
  children,
  icon,
  ...props
}) => {
  return (
    <li className="flex gap-md items-start" {...props}>
      {icon && (
        <span className="w-xl h-xl flex items-center justify-center shrink-0">
          {icon}
        </span>
      )}
      <span className="flex-1 min-w-0">{children}</span>
    </li>
  );
};

export default RetailerInfoSection;
